import { z } from 'zod'

import { ModuleTypeEnum } from './module'
import { ModuleReferenceInfoSchema } from './module-reference'
import { ThreadCommentVisibilityEnum } from './thread-comment-visibility'
import { ThreadVisibilityEnum } from './thread-membership'
import { ReactionGroup, ThreadReactionType } from './thread-reaction'

// Thread type enum
export enum ThreadTypeEnum {
  DISCUSSION = 'DISCUSSION',
  COMMENT = 'COMMENT',
  SYSTEM = 'SYSTEM',
  DIRECT_MESSAGE = 'DIRECT_MESSAGE',
}

// Thread sort field enum
export enum ThreadSortFieldEnum {
  TITLE = 'title',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
}

// Thread query parameters type
export interface ThreadQueryParams {
  page?: number
  limit?: number
  sortBy?: ThreadSortFieldEnum
  sortOrder?: 'asc' | 'desc'
  moduleType?: ModuleTypeEnum
  moduleId?: string
  threadType?: ThreadTypeEnum
  [key: string]: string | number | undefined
}

export const ThreadUserSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  displayName: z.string().nullable(),
  image: z.string().nullable(),
})

// Legacy reaction summary - kept for backward compatibility
export const ThreadReactionSummarySchema = z.object({
  id: z.string().uuid(),
  reaction: z.nativeEnum(ThreadReactionType).optional(),
  emoji: z.string().optional(), // New emoji-based reaction
})

// Reaction group schema for Slack-style emoji reactions
export const ReactionGroupSchema = z.object({
  emoji: z.string(),
  nativeEmoji: z.string(),
  count: z.number(),
  hasReacted: z.boolean(),
  reactors: z
    .array(
      z.object({
        id: z.string(),
        displayName: z.string(),
        image: z.string().nullable().optional(),
      }),
    )
    .optional(),
})

// Thread member user info schema
export const ThreadMemberUserSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable().optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
})

// Thread member schema for visibility checking and display
export const ThreadMemberBasicSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  status: z.string(),
  role: z.string(),
  user: ThreadMemberUserSchema.optional(),
})

// Last comment preview schema
export const LastCommentPreviewSchema = z.object({
  content: z.string(),
  authorId: z.string().uuid().optional(),
  authorName: z.string(),
  createdAt: z.date(),
})

// Thread schema
export const ThreadSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  userId: z.string().uuid().nullable().optional(), // Creator ID for visibility checking
  title: z.string(),
  description: z.string(),
  threadType: z.nativeEnum(ThreadTypeEnum).nullable().optional(),
  moduleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  moduleInfo: ModuleReferenceInfoSchema.nullable().optional(),
  // Membership fields
  visibility: z
    .nativeEnum(ThreadVisibilityEnum)
    .default(ThreadVisibilityEnum.PRIVATE),
  totalMembers: z.number().default(1),
  members: z.array(ThreadMemberBasicSchema).optional(), // For visibility checking
  // Existing reaction and comment fields
  user: ThreadUserSchema,
  userReaction: ThreadReactionSummarySchema.nullable(),
  reactionGroups: z.array(ReactionGroupSchema).optional(), // Slack-style grouped reactions
  totalReactions: z.number(),
  totalComments: z.number(),
  // Last comment preview for efficient list display
  lastCommentPreview: LastCommentPreviewSchema.nullable().optional(),
  // Unread indicator - true if thread has new messages since user last read
  hasUnread: z.boolean().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
})

export type ThreadUser = z.infer<typeof ThreadUserSchema>
export type ThreadReactionSummary = z.infer<typeof ThreadReactionSummarySchema>
export type ThreadMemberUser = z.infer<typeof ThreadMemberUserSchema>
export type ThreadMemberBasic = z.infer<typeof ThreadMemberBasicSchema>
export type LastCommentPreview = z.infer<typeof LastCommentPreviewSchema>
export type Thread = z.infer<typeof ThreadSchema>
/** Creation acknowledgement; readers remain responsible for current content. */
export type ThreadCreationResult = Thread & { initialMessageId?: string }

// Create Thread schema
export const ThreadWriteSchema = ThreadSchema.pick({
  title: true,
  description: true,
  visibility: true,
  threadType: true,
  moduleType: true,
  moduleId: true,
})
// Creation identity is additive for older clients. New clients retain the same
// operation and expected account until the saved outcome has been resolved.
export const CreateThreadSchema = ThreadWriteSchema.extend({
  operationId: z.string().uuid().optional(),
  expectedUserId: z.string().uuid().optional(),
  initialMessage: z
    .object({
      content: z.string(),
      clientMessageId: z.string().uuid(),
      visibility: z
        .nativeEnum(ThreadCommentVisibilityEnum)
        .default(ThreadCommentVisibilityEnum.INTERNAL),
    })
    .optional(),
})

export type CreateThread = z.infer<typeof CreateThreadSchema>

// Update Thread schema
export const UpdateThreadSchema = ThreadWriteSchema.partial()

export type UpdateThread = z.infer<typeof UpdateThreadSchema>

// Update Thread input schema/type for API + UI payloads
export const UpdateThreadInputSchema = UpdateThreadSchema

export type UpdateThreadInput = z.infer<typeof UpdateThreadInputSchema>

// Thread response schema
export const ThreadResponseSchema = z.object({
  data: ThreadSchema,
  message: z.string().optional(),
})

export type ThreadResponse = z.infer<typeof ThreadResponseSchema>

// Threads response schema
export const ThreadsResponseSchema = z.object({
  data: z.array(ThreadSchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type ThreadsResponse = z.infer<typeof ThreadsResponseSchema>
